import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    MapPin, Clock, Zap, Heart, ArrowRight,
    X, Check, Shield, Wallet, Share2, Star,
} from 'lucide-react';

function getToken() {
    return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
}

const JobDetailModal = ({ jobId, onClose, isSaved = false, onToggleSave }) => {
    const [job, setJob]           = useState(null);
    const [loading, setLoading]   = useState(true);
    const [applying, setApplying] = useState(false);
    const [applied, setApplied]   = useState(false);
    const [applyErr, setApplyErr] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        const fn = e => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', fn);
        return () => document.removeEventListener('keydown', fn);
    }, [onClose]);

    useEffect(() => {
        const token = getToken();
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/${jobId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        .then(r => setJob(r.data))
        .catch(() => setJob(null))
        .finally(() => setLoading(false));
    }, [jobId]);

    const handleApply = async () => {
        const token = getToken();
        if (!token) { onClose(); navigate('/login'); return; }
        setApplying(true); setApplyErr('');
        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/jobs/${jobId}/apply`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setApplied(true);
        } catch (e) {
            const msg = e.response?.data;
            setApplyErr(typeof msg === 'string' ? msg : 'Failed to submit application.');
        } finally {
            setApplying(false);
        }
    };

    const handleShare = () => {
        const url = `${window.location.origin}/get-job/${jobId}`;
        if (navigator.share) navigator.share({ title: job?.title, url }).catch(() => {});
        else navigator.clipboard?.writeText(url);
    };

    const tags = job?.tags ? job.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
    const reqs = job?.requirements ? job.requirements.split('||').map(r => r.trim()).filter(Boolean) : [];
    const pName = (() => {
        const u = job?.createdBy;
        if (!u) return 'Unknown';
        const f = u.firstname || u.firstName || '';
        const l = u.lastname  || u.lastName  || '';
        return (f || l) ? `${f} ${l}`.trim() : (u.username || 'Unknown');
    })();
    const ini = pName.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

    function formatDate(dt) {
        if (!dt) return '';
        try { return new Date(dt).toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' }); }
        catch { return dt; }
    }
    function formatTime(dt) {
        if (!dt) return '';
        try { return new Date(dt).toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' }); }
        catch { return ''; }
    }

    return (
        <div className="jd-scrim is-open" onClick={onClose}>
            <div className="jd-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">

                {/* Top bar */}
                <div className="jd-topbar">
                    <div className="jd-topbar-left">
                        {tags[0] && <span className="ml-badge ml-badge-active">{tags[0]}</span>}
                        {job?.verifiedOnly && (
                            <span className="ml-badge ml-badge-active"><Star size={10} /> Verified only</span>
                        )}
                    </div>
                    <div className="jd-topbar-right">
                        {onToggleSave && (
                            <button className="jd-back-btn" onClick={onToggleSave} aria-label={isSaved ? 'Unsave' : 'Save'}>
                                <Heart size={15} fill={isSaved ? 'currentColor' : 'none'} />
                            </button>
                        )}
                        <button className="jd-back-btn" onClick={handleShare} aria-label="Share">
                            <Share2 size={15} />
                        </button>
                        <button
                            style={{ width: 30, height: 30, border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', cursor: 'pointer', display: 'grid', placeItems: 'center', color: 'var(--ink-700)' }}
                            onClick={onClose} aria-label="Close">
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Scrollable body */}
                <div className="jd-body">
                    {loading && <div className="jd-loading">Loading…</div>}
                    {!loading && !job && <div className="jd-loading" style={{ color: 'var(--coral)' }}>Failed to load job.</div>}
                    {!loading && job && (
                        <div className="jd-grid">
                            {/* Main column */}
                            <div className="jd-main">
                                <h2 className="jd-title">{job.title}</h2>

                                <div className="jd-facts">
                                    {job.address && (
                                        <div className="jd-fact">
                                            <span className="jd-fact-icon"><MapPin size={16} /></span>
                                            <div className="jd-fact-text">
                                                <div className="jd-fact-label">Where</div>
                                                <div className="jd-fact-value">{job.address}</div>
                                            </div>
                                        </div>
                                    )}
                                    {job.dateTime && (
                                        <div className="jd-fact">
                                            <span className="jd-fact-icon"><Clock size={16} /></span>
                                            <div className="jd-fact-text">
                                                <div className="jd-fact-label">When</div>
                                                <div className="jd-fact-value">{formatDate(job.dateTime)}</div>
                                                {formatTime(job.dateTime) && <div className="jd-fact-sub">{formatTime(job.dateTime)}</div>}
                                            </div>
                                        </div>
                                    )}
                                    {job.duration && (
                                        <div className="jd-fact">
                                            <span className="jd-fact-icon"><Zap size={16} /></span>
                                            <div className="jd-fact-text">
                                                <div className="jd-fact-label">Duration</div>
                                                <div className="jd-fact-value">{job.duration}</div>
                                            </div>
                                        </div>
                                    )}
                                    {job.flex && (
                                        <div className="jd-fact">
                                            <span className="jd-fact-icon"><Clock size={16} /></span>
                                            <div className="jd-fact-text">
                                                <div className="jd-fact-label">Flexibility</div>
                                                <div className="jd-fact-value">± {job.flex}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {job.description && (
                                    <section className="jd-section">
                                        <h3 className="jd-section-title">About this job</h3>
                                        <p className="jd-desc">{job.description}</p>
                                    </section>
                                )}

                                {reqs.length > 0 && (
                                    <section className="jd-section">
                                        <h3 className="jd-section-title">Requirements</h3>
                                        <ul className="jd-reqs">
                                            {reqs.map((r, i) => (
                                                <li key={i}>
                                                    <span className="jd-req-check"><Check size={12} /></span>
                                                    <span>{r}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </section>
                                )}

                                {tags.length > 0 && (
                                    <section className="jd-section">
                                        <h3 className="jd-section-title">Tags</h3>
                                        <div className="jd-tags">
                                            {tags.map(t => <span key={t} className="ml-tag">#{t}</span>)}
                                        </div>
                                    </section>
                                )}

                                <section className="jd-section">
                                    <h3 className="jd-section-title">Posted by</h3>
                                    <div className="jd-poster-card">
                                        <div className="jd-poster-avatar" style={{ background: 'var(--teal-700)' }}>{ini}</div>
                                        <div className="jd-poster-meta">
                                            <div className="jd-poster-name">
                                                {pName}
                                                {job.createdBy?.passportUrl && (
                                                    <span className="jd-verified-pill"><Shield size={9} /> Verified</span>
                                                )}
                                            </div>
                                            <div className="jd-poster-stats">
                                                <span><Star size={11} /> 4.9</span>
                                                <span>·</span>
                                                <span>Replies in ~12 min</span>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <div className="jd-safety">
                                    <Shield size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                    <div>
                                        <div className="jd-safety-title">Interim covers payment until the job is done.</div>
                                        <div className="jd-safety-body">You only get paid once both sides mark it complete. No chasing cash.</div>
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar */}
                            <aside className="jd-side">
                                <div className="jd-pay-card">
                                    <div className="jd-pay-eyebrow">Total payment</div>
                                    <div className="jd-pay-amount">€{job.payment?.toFixed(0) ?? '—'}</div>
                                    <div className="jd-pay-meta">Fixed price{job.duration ? ` · ${job.duration}` : ''}</div>
                                    <div className="jd-pay-divider" />
                                    {applied ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0', color: 'var(--white)', fontSize: 14, fontWeight: 500 }}>
                                            <Check size={16} /> Application submitted!
                                        </div>
                                    ) : (
                                        <>
                                            <button className="ix-btn ix-btn-lg jd-apply-btn" onClick={handleApply} disabled={applying}>
                                                {applying ? 'Submitting…' : <><span>Apply for this job</span> <ArrowRight size={16} /></>}
                                            </button>
                                            {applyErr && (
                                                <div style={{ fontSize: 12, color: 'var(--coral)', marginTop: 8, textAlign: 'center' }}>{applyErr}</div>
                                            )}
                                            <div className="jd-pay-foot">Free to apply · No fees for workers</div>
                                        </>
                                    )}
                                </div>

                                <div className="jd-side-card">
                                    <div className="jd-side-row">
                                        <span className="jd-side-row-l"><Wallet size={14} /> Payment</span>
                                        <span className="mono" style={{ fontSize: 12 }}>€{job.payment?.toFixed(2)}</span>
                                    </div>
                                    {job.duration && (
                                        <div className="jd-side-row">
                                            <span className="jd-side-row-l"><Clock size={14} /> Duration</span>
                                            <span className="mono" style={{ fontSize: 12 }}>{job.duration}</span>
                                        </div>
                                    )}
                                    <div className="jd-side-row">
                                        <span className="jd-side-row-l"><Shield size={14} /> Verified only</span>
                                        <span className="mono" style={{ fontSize: 12 }}>{job.verifiedOnly ? 'Yes' : 'No'}</span>
                                    </div>
                                </div>

                                <button className="jd-report">Report this listing</button>
                            </aside>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobDetailModal;
